import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

import {
  notificationSettingsQueryKeys,
  notificationVariablesApi,
  type NotificationVariableRow,
} from "@/shared/api/notificationSettings";
import { Pagination } from "@/shared/ui/Pagination";

/**
 * #18935 §16 — the variable catalogue, for looking at.
 *
 * Two owners meet on this screen and the labels say so. This system decides which events can supply
 * a field; Addy Message decides how it is written, and holds the legacy `_TEN_KHACH_HANG_`
 * spellings, which exist because older Zalo templates named parameters that way. Neither half is
 * useful alone: a label with no event says nothing about whether a template can use the field, and
 * an event list with no label is a screen of dotted identifiers.
 *
 * Deliberately read-only. A variable exists because code fills it, so a screen that could add
 * `abc.foo.bar` would be a screen for describing a field that never arrives.
 */
export function NotificationVariablesPage() {
  const [object, setObject] = useState("");
  const [event, setEvent] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(0);
  const pageSize = 20;

  const { data, isLoading, isError } = useQuery({
    queryKey: notificationSettingsQueryKeys.variables(),
    queryFn: notificationVariablesApi.list,
  });

  const rows: NotificationVariableRow[] = useMemo(() => data ?? [], [data]);

  const objects = useMemo(
    () => Array.from(new Set(rows.map((r) => r.object))).sort(),
    [rows],
  );
  const events = useMemo(
    () => Array.from(new Set(rows.flatMap((r) => r.events))).sort(),
    [rows],
  );

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (object && r.object !== object) return false;
      if (event && !r.events.includes(event)) return false;
      if (!needle) return true;
      return (
        r.key.toLowerCase().includes(needle) ||
        r.label.toLowerCase().includes(needle) ||
        r.aliases.some((a) => a.toLowerCase().includes(needle))
      );
    });
  }, [rows, object, event, q]);

  // Đổi bộ lọc thì về trang đầu. Không làm thì lọc còn 3 dòng trong khi đang đứng ở trang 2 sẽ ra
  // một bảng trống, và người dùng đọc đó là "không có kết quả".
  useEffect(() => {
    setPage(0);
  }, [object, event, q]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageRows = useMemo(
    () => filtered.slice(page * pageSize, (page + 1) * pageSize),
    [filtered, page],
  );

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold">Biến nghiệp vụ</h1>
        <p className="text-sm text-muted-foreground">
          Vốn từ mà mẫu tin nhắn được viết bằng. Màn hình này để <b>xem và tra cứu</b> — không tạo
          được biến ở đây, vì một biến tồn tại là vì có mã nguồn điền nó.
        </p>
      </div>

      {/* Nguồn dữ liệu nói thẳng: không nói ra thì người dùng sẽ đi tìm nhầm chỗ khi một biến trống. */}
      {isError || (!isLoading && rows.length === 0) ? (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          Chưa lấy được danh mục từ Addy Message. Nhãn, ví dụ và tên gọi cũ do nền tảng nhắn tin
          giữ, nên khi không kết nối được thì bảng này trống — không phải là không có biến nào.
        </div>
      ) : null}

      <div className="flex flex-wrap items-end gap-3">
        <label className="grid gap-1 text-sm">
          <span className="text-xs text-muted-foreground">Nhóm</span>
          <select
            className="h-9 rounded-md border px-2"
            value={object}
            onChange={(e) => setObject(e.target.value)}
          >
            <option value="">Tất cả</option>
            {objects.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        </label>
        <label className="grid gap-1 text-sm">
          <span className="text-xs text-muted-foreground">Sự kiện</span>
          <select
            className="h-9 rounded-md border px-2"
            value={event}
            onChange={(e) => setEvent(e.target.value)}
          >
            <option value="">Tất cả</option>
            {events.map((e) => (
              <option key={e} value={e}>
                {e}
              </option>
            ))}
          </select>
        </label>
        <label className="grid gap-1 text-sm">
          <span className="text-xs text-muted-foreground">Tìm (tên, nhãn, tên cũ)</span>
          <input
            className="h-9 w-64 rounded-md border px-2"
            placeholder="patient hoặc _TEN_"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </label>
      </div>

      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
            <tr>
              <th className="px-3 py-2">Nhóm</th>
              <th className="px-3 py-2">Canonical</th>
              <th className="px-3 py-2">Nhãn</th>
              <th className="px-3 py-2">Alias</th>
              <th className="px-3 py-2">Sự kiện cung cấp</th>
              <th className="px-3 py-2 text-right">Mẫu dùng</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">
                  Đang tải…
                </td>
              </tr>
            ) : null}
            {pageRows.map((r) => (
              <tr key={r.key} className="border-t align-top">
                <td className="px-3 py-2 text-xs text-muted-foreground">{r.object}</td>
                <td className="whitespace-nowrap px-3 py-2 font-mono text-xs">
                  {`{{${r.key}}}`}
                </td>
                <td className="px-3 py-2">
                  <div>{r.label}</div>
                  <div className="text-xs text-muted-foreground">{r.description}</div>
                  <div className="text-xs text-muted-foreground/80">Ví dụ: {r.example}</div>
                </td>
                <td className="px-3 py-2 font-mono text-xs text-muted-foreground">
                  {r.aliases.length ? r.aliases.join(", ") : "—"}
                </td>
                <td className="px-3 py-2 text-xs">
                  {r.events.length ? (
                    r.events.map((e) => <div key={e}>{e}</div>)
                  ) : (
                    /* Không sự kiện nào điền: biến vẫn dùng được trong mẫu nếu nền tảng tự điền
                       (clinic.*), còn lại thì nó sẽ trống khi gửi. */
                    <span className="text-muted-foreground">
                      {r.suppliedByThisSystem ? "—" : "không sự kiện nào"}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-right text-xs">{r.templateUsage}</td>
              </tr>
            ))}
            {!isLoading && filtered.length === 0 && rows.length > 0 ? (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">
                  Không có biến nào khớp bộ lọc.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <Pagination
        page={page}
        totalPages={totalPages}
        totalElements={filtered.length}
        pageSize={pageSize}
        onChange={setPage}
      />

      <p className="max-w-3xl text-xs text-muted-foreground">
        <b>Sở hữu:</b> Addy HIS quyết định sự kiện nào cung cấp trường nào và gửi giá trị thật. Addy
        Message giữ nhãn, alias và việc dựng nội dung tin. Cột &quot;Mẫu dùng&quot; đếm cả hai
        cách viết — một mẫu viết <code>_TEN_KHACH_HANG_</code> vẫn là đang dùng{" "}
        <code>patient.name</code>.
      </p>
    </div>
  );
}
